import { useEffect, useMemo, useState } from 'react';
import { ApiError, api } from '../api';
import { CAPTION_SIZES, captionHighlightColor, DEFAULT_PLAYER_SETTINGS, validPlayerColor } from '../playerSettings';
import type { CaptionCue, CaptionTrack, PlayerSettings, VideoItem } from '../types';
import { formatDuration } from '../utils';
import { Icon } from './Icon';
import { TranscriptionPanel } from './TranscriptionPanel';

interface CaptionTracksPanelProps {
  videoId: string;
  durationSeconds: number;
  canEdit: boolean;
  playerSettings?: PlayerSettings;
  onChanged?: (tracks: CaptionTrack[]) => void;
  onVideoUpdated?: (video: VideoItem) => void;
  onSeek?: (seconds: number) => void;
}

const languagePattern = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;

export function CaptionTracksPanel({ videoId, durationSeconds, canEdit, playerSettings, onChanged, onVideoUpdated, onSeek }: CaptionTracksPanelProps) {
  const [tracks, setTracks] = useState<CaptionTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('Indonesian');
  const [languageCode, setLanguageCode] = useState('id');
  const [kind, setKind] = useState<CaptionTrack['kind']>('subtitles');
  const [isDefault, setIsDefault] = useState(true);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [addMode, setAddMode] = useState<'upload' | 'generate'>('upload');
  const [editing, setEditing] = useState<CaptionTrack | null>(null);
  const [transcriptTrackId, setTranscriptTrackId] = useState('');
  const [transcriptCues, setTranscriptCues] = useState<CaptionCue[]>([]);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState('');
  const [transcriptQuery, setTranscriptQuery] = useState('');
  const [editingCue, setEditingCue] = useState<{ index: number; text: string } | null>(null);
  const [cueBusy, setCueBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const next = await api.captionTracks(videoId);
      setTracks(next);
      setTranscriptTrackId((current) => next.some((track) => track.id === current)
        ? current : (next.find((track) => track.isDefault) ?? next[0])?.id ?? '');
      onChanged?.(next);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to load caption tracks.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [videoId]);

  useEffect(() => {
    setTranscriptQuery('');
    setEditingCue(null);
    if (!transcriptTrackId) {
      setTranscriptCues([]);
      setTranscriptError('');
      return;
    }
    let active = true;
    setTranscriptLoading(true);
    setTranscriptError('');
    api.captionTranscript(videoId, transcriptTrackId)
      .then((cues) => { if (active) setTranscriptCues(cues); })
      .catch((reason) => {
        if (active) setTranscriptError(reason instanceof ApiError ? reason.message : 'Unable to load the transcript.');
      })
      .finally(() => { if (active) setTranscriptLoading(false); });
    return () => { active = false; };
  }, [videoId, transcriptTrackId]);

  const visibleCues = useMemo(() => {
    const query = transcriptQuery.trim().toLocaleLowerCase();
    return transcriptCues.map((cue, index) => ({ cue, index })).filter(({ cue }) => !query
      || `${cue.speaker ?? ''} ${cue.text}`.toLocaleLowerCase().includes(query));
  }, [transcriptCues, transcriptQuery]);

  async function saveCue() {
    if (!editingCue || !transcriptTrackId) return;
    const nextText = editingCue.text.trim();
    if (!nextText) { setTranscriptError('Caption text cannot be empty.'); return; }
    setCueBusy(true);
    setTranscriptError('');
    try {
      const result = await api.updateCaptionCue(videoId, transcriptTrackId, editingCue.index, nextText);
      setTranscriptCues((current) => current.map((cue, index) => index === editingCue.index ? result.cue : cue));
      const nextTracks = tracks.map((track) => track.id === result.track.id ? result.track : track);
      setTracks(nextTracks);
      setEditingCue(null);
      onChanged?.(nextTracks);
    } catch (reason) {
      setTranscriptError(reason instanceof ApiError ? reason.message : 'Unable to save this subtitle line.');
    } finally {
      setCueBusy(false);
    }
  }

  async function upload() {
    if (!file) { setError('Choose a .vtt or .srt file.'); return; }
    if (!label.trim()) { setError('Enter a track label.'); return; }
    if (!languagePattern.test(languageCode.trim())) { setError('Use a language code such as id or en-US.'); return; }
    setBusy(true);
    setError('');
    try {
      await api.uploadCaptionTrack(videoId, { file, label: label.trim(), languageCode: languageCode.trim(), kind, isDefault, replaceExisting });
      setFile(null);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to upload captions.');
    } finally {
      setBusy(false);
    }
  }

  async function update(track: CaptionTrack, changes: Partial<Pick<CaptionTrack, 'label' | 'languageCode' | 'kind' | 'isDefault'>>) {
    setBusy(true);
    setError('');
    try {
      await api.updateCaptionTrack(videoId, track.id, changes);
      setEditing(null);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to update the caption track.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(track: CaptionTrack) {
    setBusy(true);
    setError('');
    try {
      await api.deleteCaptionTrack(videoId, track.id);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to delete the caption track.');
    } finally {
      setBusy(false);
    }
  }

  return <div className="caption-panel">
    <section className="video-details__card">
      <div className="video-details__section-heading"><div><span>Accessibility</span><h3>Captions and subtitles</h3></div></div>
      <p className="video-details__explanation">Upload WebVTT or SRT. Rolling Google Meet captions are normalized so only one readable cue appears at a time.</p>
      {error && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{error}</p>}
      {loading ? <div className="video-details__links-loading"><span className="spinner spinner--large" />Loading captions…</div> : tracks.length ? <div className="caption-panel__tracks">
        {tracks.map((track) => <article key={track.id} className="caption-track">
          {editing?.id === track.id ? <CaptionEditForm track={editing} busy={busy} onCancel={() => setEditing(null)} onSave={(changes) => void update(track, changes)} /> : <>
            <div className="caption-track__identity">
              <strong>{track.label}</strong>
              <span>{track.languageCode} · {track.kind === 'captions' ? 'Captions' : 'Subtitles'}{track.isDefault ? ' · Default' : ''}</span>
              <small>{track.cueCount.toLocaleString()} cues · through {formatDuration(track.durationSeconds)} · {track.source === 'transcription' ? 'AI transcription' : 'Uploaded file'}</small>
            </div>
            {canEdit && <div className="caption-track__actions">
              <a href={`/api/videos/${encodeURIComponent(videoId)}/captions/${encodeURIComponent(track.id)}/download`}>Download</a>
              <button type="button" disabled={busy} aria-pressed={transcriptTrackId === track.id} onClick={() => setTranscriptTrackId(track.id)}>View transcript</button>
              {!track.isDefault && <button type="button" disabled={busy} onClick={() => void update(track, { isDefault: true })}>Set default</button>}
              <button type="button" disabled={busy} onClick={() => setEditing(track)}>Edit</button>
              <button type="button" className="danger" disabled={busy} onClick={() => void remove(track)}>Delete</button>
            </div>}
          </>}
        </article>)}
      </div> : <div className="caption-panel__empty"><Icon name="film" size={25} /><strong>No caption tracks</strong><span>Viewers will not see a CC option until a track is added.</span></div>}
      {!!tracks.length && <section className="caption-transcript" aria-labelledby={`transcript-heading-${videoId}`}>
        <div className="caption-transcript__heading">
          <div><span>Transcript</span><h4 id={`transcript-heading-${videoId}`}>{tracks.find((track) => track.id === transcriptTrackId)?.label ?? 'Caption track'}</h4></div>
          <label><span className="sr-only">Search transcript</span><input type="search" aria-label="Search transcript" placeholder="Search transcript" value={transcriptQuery} onChange={(event) => setTranscriptQuery(event.target.value)} /></label>
        </div>
        {transcriptError && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{transcriptError}</p>}
        {transcriptLoading ? <div className="video-details__links-loading"><span className="spinner" />Loading transcript…</div>
          : visibleCues.length ? <div className="caption-transcript__cues">
            {visibleCues.map(({ cue, index }) => <article key={`${cue.startSeconds}:${index}`} className={editingCue?.index === index ? 'is-editing' : ''}>
              {editingCue?.index === index ? <div className="caption-transcript__editor">
                <time>{formatDuration(cue.startSeconds)}</time>
                <label><span className="sr-only">Edit subtitle at {formatDuration(cue.startSeconds)}</span><textarea autoFocus aria-label={`Edit subtitle at ${formatDuration(cue.startSeconds)}`} rows={2} maxLength={2000} value={editingCue.text} onChange={(event) => setEditingCue({ index, text: event.target.value })} /></label>
                <div><button type="button" className="button button--secondary" disabled={cueBusy} onClick={() => setEditingCue(null)}>Cancel</button><button type="button" className="button button--primary" disabled={cueBusy || !editingCue.text.trim()} onClick={() => void saveCue()}>{cueBusy ? <span className="spinner" /> : <Icon name="check" size={15} />}Save</button></div>
              </div> : <>
                <button type="button" className="caption-transcript__seek" onClick={() => onSeek?.(cue.startSeconds)}>
                  <time>{formatDuration(cue.startSeconds)}</time><span>{cue.speaker && <strong>{cue.speaker}</strong>}{cue.text}</span>
                </button>
                {canEdit && <button type="button" className="caption-transcript__edit-button" aria-label={`Edit subtitle at ${formatDuration(cue.startSeconds)}`} onClick={() => setEditingCue({ index, text: cue.text })}><Icon name="edit" size={15} /></button>}
              </>}
            </article>)}
          </div> : <p className="caption-transcript__empty">{transcriptQuery ? 'No transcript cues match this search.' : 'This track has no readable cues.'}</p>}
      </section>}
    </section>

    {canEdit && <aside className="caption-panel__side">
      <CaptionAppearanceEditor videoId={videoId} settings={playerSettings ?? DEFAULT_PLAYER_SETTINGS} onUpdated={onVideoUpdated} />
      <section className="video-details__card caption-panel__add">
      <div className="caption-panel__source-tabs" role="tablist" aria-label="Add caption method">
        <button id={`caption-upload-tab-${videoId}`} type="button" role="tab" aria-selected={addMode === 'upload'} aria-controls={`caption-upload-panel-${videoId}`} className={addMode === 'upload' ? 'is-active' : ''} onClick={() => setAddMode('upload')}><Icon name="upload" size={16} />Upload file</button>
        <button id={`caption-generate-tab-${videoId}`} type="button" role="tab" aria-selected={addMode === 'generate'} aria-controls={`caption-generate-panel-${videoId}`} className={addMode === 'generate' ? 'is-active' : ''} onClick={() => setAddMode('generate')}><Icon name="server" size={16} />AI subtitles</button>
      </div>
      <div id={`caption-upload-panel-${videoId}`} className="caption-panel__source caption-panel__upload" role="tabpanel" aria-labelledby={`caption-upload-tab-${videoId}`} hidden={addMode !== 'upload'}>
        <div className="video-details__section-heading"><div><span>Add track</span><h3>Upload VTT or SRT</h3></div></div>
        <div className="caption-panel__form">
          <label className="caption-panel__file"><span>Caption file</span><input aria-label="Caption file" type="file" accept=".vtt,.srt,text/vtt,application/x-subrip" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /><small>{file?.name ?? 'Maximum 2 MB. The original video is unchanged.'}</small></label>
          <label><span>Label</span><input aria-label="Caption label" value={label} maxLength={80} onChange={(event) => setLabel(event.target.value)} /></label>
          <label><span>Language code</span><input aria-label="Caption language code" value={languageCode} maxLength={35} onChange={(event) => setLanguageCode(event.target.value)} /></label>
          <label><span>Track type</span><select className="control-select" aria-label="Caption track type" value={kind} onChange={(event) => setKind(event.target.value as CaptionTrack['kind'])}><option value="subtitles">Subtitles</option><option value="captions">Captions</option></select></label>
          <label className="checkbox-field caption-panel__default"><input className="control-choice" type="checkbox" checked={isDefault} onChange={(event) => setIsDefault(event.target.checked)} /><span>Enable by default<small>Viewers can still turn captions off in the player.</small></span></label>
          <label className="checkbox-field caption-panel__default"><input className="control-choice" type="checkbox" checked={replaceExisting} onChange={(event) => setReplaceExisting(event.target.checked)} /><span>Replace same-language track<small>Replaces the existing track only after this file passes validation.</small></span></label>
        </div>
        <button type="button" className="button button--primary" disabled={busy || !file} onClick={() => void upload()}>{busy ? <span className="spinner" /> : <Icon name="upload" size={16} />}Upload track</button>
      </div>
      <div id={`caption-generate-panel-${videoId}`} className="caption-panel__source" role="tabpanel" aria-labelledby={`caption-generate-tab-${videoId}`} hidden={addMode !== 'generate'}>
        <TranscriptionPanel videoId={videoId} durationSeconds={durationSeconds} onCompleted={() => void load()} />
      </div>
      </section>
    </aside>}
  </div>;
}

function CaptionAppearanceEditor({ videoId, settings: current, onUpdated }: {
  videoId: string;
  settings: PlayerSettings;
  onUpdated?: (video: VideoItem) => void;
}) {
  const [settings, setSettings] = useState<PlayerSettings>({ ...current });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSettings({ ...current });
    setError('');
  }, [videoId, current.captionTextColor, current.captionBackgroundColor, current.captionSize]);

  function change<K extends keyof PlayerSettings>(key: K, value: PlayerSettings[K]) {
    setSettings((previous) => ({ ...previous, [key]: value }));
    setSaved(false);
  }

  async function save() {
    const captionTextColor = settings.captionTextColor.toUpperCase();
    const captionBackgroundColor = settings.captionBackgroundColor.toUpperCase();
    if (!validPlayerColor(captionTextColor) || !validPlayerColor(captionBackgroundColor)) {
      setError('Text and highlight colors must use six-digit hex values such as #FFFFFF.');
      return;
    }
    const next = { ...settings, captionTextColor, captionBackgroundColor };
    setSaving(true);
    setError('');
    try {
      const updated = await api.updateVideo(videoId, { playerSettings: next });
      setSettings(next);
      setSaved(true);
      onUpdated?.(updated);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Unable to save subtitle appearance.');
    } finally {
      setSaving(false);
    }
  }

  const dirty = settings.captionTextColor.toUpperCase() !== current.captionTextColor.toUpperCase()
    || settings.captionBackgroundColor.toUpperCase() !== current.captionBackgroundColor.toUpperCase()
    || settings.captionSize !== current.captionSize;
  const previewSize = settings.captionSize === 'small' ? '.78rem' : settings.captionSize === 'large' ? '1.08rem' : '.9rem';

  return <section className="video-details__card caption-appearance">
    <div className="video-details__section-heading"><div><span>Player style</span><h3>Subtitle appearance</h3></div></div>
    <p className="video-details__explanation">Applied to this video's dashboard preview and every published embed.</p>
    <div className="caption-appearance__sample" aria-label="Subtitle appearance preview"><span style={{ color: validPlayerColor(settings.captionTextColor) ? settings.captionTextColor : '#FFFFFF', backgroundColor: captionHighlightColor(settings.captionBackgroundColor), fontSize: previewSize }}>Subtitle preview</span></div>
    <div className="caption-appearance__fields">
      <label><span>Text color</span><div className="caption-appearance__color"><input className="control-color" type="color" aria-label="Choose subtitle text color" value={validPlayerColor(settings.captionTextColor) ? settings.captionTextColor : DEFAULT_PLAYER_SETTINGS.captionTextColor} onChange={(event) => change('captionTextColor', event.target.value.toUpperCase())} /><input aria-label="Subtitle text color" maxLength={7} value={settings.captionTextColor} onChange={(event) => change('captionTextColor', event.target.value)} spellCheck={false} /></div></label>
      <label><span>Highlight color</span><div className="caption-appearance__color"><input className="control-color" type="color" aria-label="Choose subtitle highlight color" value={validPlayerColor(settings.captionBackgroundColor) ? settings.captionBackgroundColor : DEFAULT_PLAYER_SETTINGS.captionBackgroundColor} onChange={(event) => change('captionBackgroundColor', event.target.value.toUpperCase())} /><input aria-label="Subtitle highlight color" maxLength={7} value={settings.captionBackgroundColor} onChange={(event) => change('captionBackgroundColor', event.target.value)} spellCheck={false} /></div></label>
      <label className="caption-appearance__size"><span>Text size</span><select className="control-select" aria-label="Subtitle text size" value={settings.captionSize} onChange={(event) => change('captionSize', event.target.value as PlayerSettings['captionSize'])}>{CAPTION_SIZES.map((size) => <option key={size.value} value={size.value}>{size.label}</option>)}</select></label>
    </div>
    {error && <p className="form-alert" role="alert"><Icon name="warning" size={16} />{error}</p>}
    <div className="caption-appearance__footer"><span aria-live="polite">{saved ? 'Appearance saved.' : 'Highlight uses 78% opacity for readability.'}</span><button type="button" className="button button--primary" disabled={saving || !dirty} onClick={() => void save()}>{saving ? <span className="spinner" /> : <Icon name="check" size={16} />}Save style</button></div>
  </section>;
}

function CaptionEditForm({ track, busy, onCancel, onSave }: {
  track: CaptionTrack;
  busy: boolean;
  onCancel: () => void;
  onSave: (changes: Pick<CaptionTrack, 'label' | 'languageCode' | 'kind'>) => void;
}) {
  const [label, setLabel] = useState(track.label);
  const [languageCode, setLanguageCode] = useState(track.languageCode);
  const [kind, setKind] = useState(track.kind);
  return <div className="caption-track__edit">
    <label><span>Label</span><input aria-label={`Edit label for ${track.label}`} value={label} maxLength={80} onChange={(event) => setLabel(event.target.value)} /></label>
    <label><span>Language</span><input aria-label={`Edit language for ${track.label}`} value={languageCode} maxLength={35} onChange={(event) => setLanguageCode(event.target.value)} /></label>
    <label><span>Type</span><select className="control-select" aria-label={`Edit type for ${track.label}`} value={kind} onChange={(event) => setKind(event.target.value as CaptionTrack['kind'])}><option value="subtitles">Subtitles</option><option value="captions">Captions</option></select></label>
    <div><button type="button" className="button button--secondary" disabled={busy} onClick={onCancel}>Cancel</button><button type="button" className="button button--primary" disabled={busy || !label.trim() || !languagePattern.test(languageCode.trim())} onClick={() => onSave({ label: label.trim(), languageCode: languageCode.trim(), kind })}>Save track</button></div>
  </div>;
}
